import type { WireItem } from './grid';
import type { TimelineItemKind } from '@norum/contracts';
import type { Tone } from '@/ui/primitives';
import { twinLabel } from '@/ui/people';

/** The nine kinds, as a person reads them. The set is the platform's (a CHECK constraint). */
export const KIND: Record<TimelineItemKind, { label: string; tone: Tone }> = {
  calendar_event: { label: 'Meeting', tone: 'accent' },
  task: { label: 'Task', tone: 'neutral' },
  reply_needed: { label: 'Needs a reply', tone: 'warning' },
  brief: { label: 'Brief', tone: 'accent' },
  chase: { label: 'Chase', tone: 'warning' },
  shared_inbox: { label: 'hello@', tone: 'warning' },
  merge_proposal: { label: 'Merge?', tone: 'accent' },
  break: { label: 'Break', tone: 'neutral' },
  note: { label: 'Note', tone: 'neutral' },
};

/** The horizons, in the prototype's order and words (P:1793). */
export const SPAN_LABEL = {
  day: 'Today',
  last2: 'Yesterday + today',
  next2: 'Next 2 days',
  week: 'Week',
  month: 'Month · proposed',
} as const;

export type ViewSpan = keyof typeof SPAN_LABEL;

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function dayTitle(d: Date, now: Date): string {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const that = new Date(d);
  that.setHours(0, 0, 0, 0);
  const diff = Math.round((that.getTime() - today.getTime()) / 86_400_000);
  const name = diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : diff === -1 ? 'Yesterday' : DAYS[d.getDay()]!;
  return `${name} · ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "Tuesday 22 September" — the header's kicker on Today. */
export function longDate(d: Date): string {
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "Good morning, Iryna": the prototype's Today title, by the hour and the given name. */
export function greeting(now: Date, displayName: string): string {
  const h = now.getHours();
  const part = h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
  const given = displayName.split(/\s+/)[0] ?? displayName;
  return `Good ${part}, ${given}`;
}

export function clock(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** A twin's given name from its handle: `sol.twin` → "Sol". A handle is an id, never what a person reads. */
export function twinGiven(handle: string): string {
  const given = handle.endsWith('.twin') ? handle.slice(0, -'.twin'.length) : handle;
  return given.charAt(0).toUpperCase() + given.slice(1);
}

/** "Sol · Iryna's twin" for a twin handle, the address for a worker, the name for a person. */
export function actorLabel(id: string, twinOf?: string): string {
  if (id.endsWith('.twin')) {
    const given = id.slice(0, -'.twin'.length);
    const name = given.charAt(0).toUpperCase() + given.slice(1);
    return twinOf ? twinLabel(name, twinOf) : `${name} · a twin`;
  }
  if (id.includes('.agent@')) {
    const given = id.split('.')[0]!;
    return `${given.charAt(0).toUpperCase() + given.slice(1)} Agent`;
  }
  return id;
}

/** What started an agent's placement, as the row says it (P:878-882; ADR-0058). */
export const STARTED_BY: Record<NonNullable<WireItem['origin']>, string> = {
  chat: 'your request in chat',
  confirmation: 'your confirmation',
  assignment: 'an assignment a person made',
  schedule: 'the platform’s schedule, under your standing rule',
};

export function placedLabel(item: Pick<WireItem, 'placedBy' | 'actorName' | 'kind'>): string {
  if (item.placedBy === 'human') return 'You placed it';
  if (item.placedBy === 'candidate') return 'Suggested';
  const who = item.actorName ?? 'An agent';
  return `${who} ${item.kind === 'calendar_event' ? 'booked' : 'placed'} it`;
}

